import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IEnergyProducerUnit } from '../../entities/interfaces/energyProducerUnit';
import { detectDecimalSeparator } from '../../utils/utils';
import { Document } from '../../../jurisdiction/jurisdiction.domain';
import { GridApplicationDocumentTypes } from '../../entities/enums/grid-application-document-types.enum';
import { UICollectionTypes } from '../../entities/enums/ui-collection-types.enum';
import { NgForm, ControlContainer } from '@angular/forms';
import { FileEvent } from '../../../+shared/models/fileEvent';
import { ZerezApiResponse, ZerezService } from '../../../services/zerez.service';
import { ZerezIdMask } from '../../../jurisdiction/jurisdiction.constants';
import { DestroyableComponent } from '../../../common/destroyable.component';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorService } from '../../../services/error/error.service';
import { ToastNotificationService } from '../../../+shared/services/toast-notification.service';

@Component({
  providers: [ZerezService],
  selector: 'app-eproducer-unit-form-element',
  templateUrl: './eproducer-unit-form-element.component.html',
  styleUrls: ['./eproducer-unit-form-element.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class EproducerUnitFormElementComponent extends DestroyableComponent implements OnInit {
  @Input() energyUnit: IEnergyProducerUnit;
  @Input() index: number;
  @Input() type: string;
  @Input() isDetailsView: boolean;

  private _documents: Document[];
  public get documents(): Document[] {
    return this._documents;
  }
  @Input()
  public set documents(value: Document[]) {
    this._documents = value?.sort((a, b) => parseInt(b.document_id) - parseInt(a.document_id));
  }

  @Input() isDisabled: boolean;

  @Output() removeElementEvent = new EventEmitter<any>();
  @Output() maxPowerChangeEvent = new EventEmitter<any>();
  @Output() clearFileEvent = new EventEmitter<FileEvent>();
  @Output() calculateInvertersPowerDifference = new EventEmitter<any>();

  public productDataSheet: Document;
  public unitCertificate: Document;
  public auditReport: Document;
  public documentTypes = GridApplicationDocumentTypes;
  public uiCollectionTypes = UICollectionTypes;
  public zerezIdMask = ZerezIdMask;
  public isLoading = false;

  constructor(private zerezService: ZerezService, private errorService: ErrorService, private toaster: ToastNotificationService) {
    super();
  }

  ngOnInit(): void {
    if (this.documents) {
      this.productDataSheet =
        this.documents.find(x => x.sk_document_type === GridApplicationDocumentTypes.EnergyUnitDataSheet) ?? ({} as Document);
      this.unitCertificate =
        this.documents.find(x => x.sk_document_type === GridApplicationDocumentTypes.UnitCertificate) ?? ({} as Document);
      this.auditReport = this.documents.find(x => x.sk_document_type === GridApplicationDocumentTypes.AuditReport) ?? ({} as Document);
    }
  }

  public removeElement(element: any): void {
    this.removeElementEvent.emit({ element, type: this.type });
  }

  public maxPowerChange(maxPowerValue: number): void {
    if (maxPowerValue > 52 && this.documents.length === 2) {
      this.documents.push(new Document(GridApplicationDocumentTypes.AuditReport));
      this.auditReport = this.documents.find(d => d.sk_document_type === GridApplicationDocumentTypes.AuditReport);
    } else if (maxPowerValue <= 52 && this.documents.length === 3) {
      this.documents.splice(this.documents.length - 1, 1);
    }

    this.maxPowerChangeEvent.emit(true);
  }

  public identicalUnitsChange(): void {
    this.maxPowerChangeEvent.emit(true);
  }

  public detectDecimalSeparator = detectDecimalSeparator;

  public clearFile(fileEvent: FileEvent): void {
    this.clearFileEvent.emit(fileEvent);
  }

  public validateZerezId() {
    this.isLoading = true;

    this.preventLeak(this.zerezService.getUnitByZerezId(this.energyUnit.zerez_id)).subscribe({
      next: res => this.validateZerezResponse(res),
      error: err => this.validateZerezResponse(err),
    });
  }

  public get isValidityConfirmed(): string | null {
    return this.energyUnit?.certificate_validity === 'Valid / Ongoing' ? 'valid' : null;
  }

  private validateZerezResponse(response: ZerezApiResponse) {
    this.isLoading = false;
    if (response instanceof HttpErrorResponse) {
      this.errorService.throwErrorIfFailure(response, true);
    }
    if (response.certificate?.certificate_validity_status) {
      this.energyUnit.manufacturer = response.model_name ?? '';
      this.energyUnit.type = response.certificate?.category ?? '';
      this.energyUnit.max_unit_power = response.maximum_active_power_kw ?? 0;
      this.energyUnit.certificate_number = response.certificate?.certificate_number ?? '';
      this.energyUnit.certificate_validity = response.certificate?.certificate_validity_status ?? '';
      this.energyUnit.certificate_status = response.certificate?.validation_status ?? '';

      if (this.isValidityConfirmed) this.toaster.success('Certificate is valid!', 5000);
      else this.toaster.error('Certificate is not valid!', 5000);
    } else {
      this.energyUnit.manufacturer = '';
      this.energyUnit.type = '';
      this.energyUnit.max_unit_power = 0;
      this.energyUnit.certificate_number = '';
      this.energyUnit.certificate_validity = '';
      this.energyUnit.certificate_status = '';

      this.toaster.error('Certificate is not valid or unit not found!', 7000);
    }
    this.calculateInvertersPowerDifference.emit();
  }
}
